/* eslint-disable import/prefer-default-export */
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import messageUtil from 'utils/messageUtil'
import LevelOneType from 'enums/LevelOneType'
import { SentimentStrings } from 'enums/Sentiment'
import { GenderStrings } from 'enums/Gender'
import { SocialWebStoryScopeWidget } from 'schemas/SocialWebStoryScopeWidgetSchema'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { DashboardWidget } from 'features/widget/types'
import { isRightToLeftText, RTL_MARK } from 'utils/rtlTextUtil'
import { SunburstFirstLevelData } from '../../../../visualization/Sunburst/types'

type LabeledInsightData = InsightData & { label: string }

export const getDisplayText = (
  name: string,
  viewOption: InsightViewOptionStrings,
  emojiNameToDisplayMap: Record<string, string>
) => {
  switch (viewOption) {
    case InsightViewOption.EMOJIS_EMOJI:
    case InsightViewOption.EMOJIS_EMOTION:
    case InsightViewOption.EMOJIS_GROUP:
      return emojiNameToDisplayMap[name] || name

    default:
      if (isRightToLeftText(name) && name.charAt(0) === '#') {
        return RTL_MARK + name
      }

      return name
  }
}

const buildSecondLevelData = (
  data: LabeledInsightData[],
  levelName: string,
  widgetSetting: SocialWebStoryScopeWidget
) => ({
  groups: data
    .filter((item) => item.value! >= widgetSetting.minCount!)
    .slice(0, widgetSetting.topN)
    .map((item) => ({
      label: item.label,
      weight: item.value!,
      firstLevel: levelName,
      secondLevel: [item],
      data: item
    }))
})

const resolveInsightLabel = (widgetSetting: SocialWebStoryScopeWidget, insight: string) => {
  const { levelOneType } = widgetSetting

  switch (levelOneType) {
    case LevelOneType.SENTIMENT:
    case LevelOneType.SENTIMENT_WO_NEUTRAL:
      return messageUtil.sentiments(insight as SentimentStrings)
    case LevelOneType.GENDER_WO_UNKNOWN:
      return messageUtil.genders(insight as GenderStrings)
    default:
      throw new Error(`Unsupported LevelOneType: ${levelOneType}`)
  }
}

const buildFirstLevelData = (
  labeledData: LabeledInsightData[][],
  widgetSetting: SocialWebStoryScopeWidget,
  insights: string[]
) => {
  const [countData, ...insightData] = labeledData
  const groups: SunburstFirstLevelData[] = []

  insights.forEach((insight, index) => {
    const data = insightData[index]
    const labeledInsightData = countData.find((item) => item.name === insight)
    const count = labeledInsightData!.value!

    if (count < widgetSetting.minCount!) {
      return
    }

    groups.push({
      label: resolveInsightLabel(widgetSetting, insight),
      weight: count,
      firstLevel: insight,
      data: labeledInsightData!,
      ...buildSecondLevelData(data, insight, widgetSetting)
    })
  })

  return { groups }
}

export const convertToSunburstData = (
  widgetData: DashboardWidget<InsightCount>,
  widgetSetting: SocialWebStoryScopeWidget,
  levelOneInsights: string[],
  emojiNameToDisplayMap: Record<string, string>
) => {
  const labeledData = widgetData.data.map((data) =>
    data.results![0].data!.insights![0].dataset![0].set!.map((item: InsightData) => ({
      ...item,
      label: getDisplayText(item!.name!, widgetSetting!.viewOption!, emojiNameToDisplayMap)
    }))
  )!

  if (!levelOneInsights.length) {
    return buildSecondLevelData(labeledData[0], '', widgetSetting)
  }

  return buildFirstLevelData(labeledData, widgetSetting, levelOneInsights)
}
